import { useEffect, useState, useMemo, useRef } from 'react';
import RapidBatchSearchApi from 'api/RapidBatchSearchApi';
import SearchFilterUtil from 'lib/SearchFilterUtil';
import { compareFilters } from 'lib/FilterComparisonUtil';
import { useSelector } from 'react-redux';
import { LISTINGS, RENTALS, SALES } from 'constants/comparableTypes';

const COMPARABLE_STATE = {
    [LISTINGS]: 'comparableListings',
    [SALES]: 'comparableSales',
    [RENTALS]: 'comparableRentals',
};

const useFetchMetadata = (open, comparableType, filters, hasError) => {
    const [totalElements, setTotalElements] = useState(0);
    const [fetching, setFetching] = useState(false);
    const {
        localityName,
        postcode,
        addressState,
        latitude,
        longitude,
        addressTown,
        comparableState,
        propertyId,
    } = useSelector(state => ({
        localityName: state.propertyDetail.localityName,
        postcode: state.propertyDetail.addressPostcode,
        singleLineAddress: state.propertyDetail.singleLineAddress,
        addressState: state.propertyDetail.addressState,
        latitude: state.propertyDetail.latitude,
        longitude: state.propertyDetail.longitude,
        propertyId: state.propertyDetail.propertyId,
        addressTown: state.propertyDetailCache?.addressTown,
        comparableState: state[COMPARABLE_STATE[comparableType]],
    }));

    const prevFiltersRef = useRef();
    const currentFilters = filters[comparableType];
    const serializedFilters = useMemo(() => {
        // If filters haven't changed (ignoring empty unit changes), return the previous serialization
        if (prevFiltersRef.current && compareFilters(prevFiltersRef.current, currentFilters)) {
            return JSON.stringify(prevFiltersRef.current);
        }

        // Store the current filters for next comparison
        prevFiltersRef.current = currentFilters;
        return JSON.stringify(currentFilters);
    }, [currentFilters]);


    useEffect(() => {
        setTotalElements(comparableState?.pagination?.totalElements);
    }, [comparableState?.pagination?.totalElements]);

    useEffect(() => {
        if (!open) return;
        if (hasError) return;

        const fetchData = async () => {
            setFetching(true);
            try {
                const { pagination: { page }, sortBy } = comparableState;
                const response = await RapidBatchSearchApi.getRapidBatchSearch({
                    addressState,
                    addressTown,
                    comparableType,
                    filters: currentFilters,
                    localityName,
                    postcode,
                    page,
                    sortBy,
                    coordinates: { lat: latitude, lon: longitude },
                    targetId: propertyId,
                    metadata: true,
                }, comparableType);

                setTotalElements(SearchFilterUtil.numberWithCommas(response?.metadata?.totalElements));
            } catch (e) {
                console.error('Error fetching comparable data:', e);
            } finally {
                setFetching(false);
            }
        };

        fetchData();
    }, [open, serializedFilters, hasError]);

    return { totalElements, fetching };
};

export default useFetchMetadata;